import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import { Button } from '@orisini/ui/components/button'
import { Card } from '@orisini/ui/components/card'
import { Switch } from '@orisini/ui/components/switch'
import { useConfirm } from '@orisini/ui/components/confirm'
import { cn } from '@orisini/ui/lib/utils'
import { contentApi, DESTINATION_LABEL, type Destination, type Workflow } from '../../content/lib/content'
import type { Workspace } from '../lib/accounts'

const MODES: { value: Workflow['mode']; title: string; hint: string; recommended?: boolean }[] = [
  {
    value: 'approval',
    title: 'Approval required',
    hint: 'Orisini writes a draft for each story you pick. You edit, ask for a rewrite, or approve before it is produced.',
    recommended: true,
  },
  { value: 'auto', title: 'Auto production', hint: 'Drafts are approved automatically and produced straight away.' },
]

const DEST_HINT: Record<Destination, string> = {
  bank: 'Finished content waits in the Content Bank. Schedule or post it when you want.',
  queue: "Finished content goes into this account's queue and posts at the next free time.",
  publish: 'Finished content is posted as soon as Orisini Agent picks it up.',
}

/**
 * Workflow project: approval produksi, approval publikasi (dua hal berbeda), dan
 * tujuan hasil. Bawaannya yang paling aman: approval + Content Bank.
 */
export function WorkflowTab({ workspace: w }: { workspace: Workspace }) {
  const confirm = useConfirm()
  const [wf, setWf] = useState<Workflow | null>(null)
  const [saved, setSaved] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    contentApi
      .workflow(w.id)
      .then((r) => {
        setWf(r.workflow)
        setSaved(JSON.stringify(r.workflow))
      })
      .catch((err) => toast.error(err instanceof Error ? err.message : "Couldn't load the workflow"))
  }, [w.id])

  if (!wf) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Loading…
      </div>
    )
  }

  const pilihTujuan = async (d: Destination) => {
    if (d === 'publish' && !wf.requirePublishApproval) {
      const ok = await confirm({
        title: 'Publish without checking?',
        description:
          wf.mode === 'auto'
            ? 'With auto production on, stories you pick will be written, designed, and posted with nobody looking at them first.'
            : 'Approved drafts will be posted as soon as they are produced.',
        confirmLabel: 'Publish immediately',
      })
      if (!ok) return
    }
    setWf({ ...wf, destination: d })
  }

  const simpan = async () => {
    setSaving(true)
    try {
      const r = await contentApi.saveWorkflow(w.id, wf)
      setWf(r)
      setSaved(JSON.stringify(r))
      toast.success('Workflow saved')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't save the workflow")
    } finally {
      setSaving(false)
    }
  }

  const Toggle = ({ k, title, hint }: { k: 'allowEdit' | 'allowRegenerate' | 'requirePublishApproval'; title: string; hint: string }) => (
    <div className="flex items-center gap-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      <Switch checked={wf[k]} onCheckedChange={(v) => setWf({ ...wf, [k]: v })} aria-label={title} />
    </div>
  )

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <h2 className="text-sm font-semibold">How should Orisini process the stories you pick?</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {MODES.map((m) => (
            <button
              key={m.value}
              type="button"
              aria-pressed={wf.mode === m.value}
              onClick={() => setWf({ ...wf, mode: m.value })}
              className={cn('space-y-1 rounded-lg border p-4 text-left', wf.mode === m.value ? 'border-primary bg-primary/5' : 'border-border hover:border-foreground/40')}
            >
              <span className="flex items-center gap-2 font-medium">
                {m.title}
                {m.recommended && <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">Recommended</span>}
              </span>
              <span className="block text-xs text-muted-foreground">{m.hint}</span>
            </button>
          ))}
        </div>
      </section>

      <Card className="divide-y divide-border px-4">
        <Toggle k="allowEdit" title="Allow editing" hint="Change the words and photo of a draft or finished post." />
        <Toggle k="allowRegenerate" title="Allow rewriting" hint="Ask Orisini to write a draft again from the same story." />
        <Toggle
          k="requirePublishApproval"
          title="Ask before publishing"
          hint="Even when production is automatic, nothing is queued or posted until you approve it in Review."
        />
      </Card>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold">Where does finished content go?</h2>
        <div className="grid gap-3 sm:grid-cols-3">
          {(Object.keys(DESTINATION_LABEL) as Destination[]).map((d) => (
            <button
              key={d}
              type="button"
              aria-pressed={wf.destination === d}
              onClick={() => void pilihTujuan(d)}
              className={cn('space-y-1 rounded-lg border p-4 text-left', wf.destination === d ? 'border-primary bg-primary/5' : 'border-border hover:border-foreground/40')}
            >
              <span className="block text-sm font-medium">{DESTINATION_LABEL[d]}</span>
              <span className="block text-xs text-muted-foreground">{DEST_HINT[d]}</span>
            </button>
          ))}
        </div>
        {!w.productCode && (
          <p className="text-xs text-muted-foreground">This project has no account yet, so finished content stays in the Content Bank whatever you choose here.</p>
        )}
        {w.productCode === 'pinboss' && (
          <p className="text-xs text-muted-foreground">Pinterest takes single images, so carousels from this account always stay in the Content Bank.</p>
        )}
      </section>

      <div className="flex items-center gap-2">
        <Button onClick={simpan} disabled={saving || JSON.stringify(wf) === saved}>
          {saving && <Loader2 className="animate-spin" />}
          Save workflow
        </Button>
        {JSON.stringify(wf) !== saved && (
          <Button variant="ghost" disabled={saving} onClick={() => setWf(JSON.parse(saved) as Workflow)}>
            Discard changes
          </Button>
        )}
      </div>
    </div>
  )
}
